import {
  hasKitchenBindToken,
  hasKitchenPwaManifestFlag,
  isKitchenSetupComplete,
  markKitchenPwaManifest,
} from '@/utils/kitchenBinding';
import { hasValidKitchenSession } from '@/utils/kitchenSession';
import { isKitchenRoute } from '@/utils/authUtils';
import { applyKitchenPwaHead, restorePartnerPwaHead } from '@/utils/kitchenPwaHead';
import { shouldUseKitchenInstallWizard } from '@/utils/kitchenInstallWizard';
import { needsKitchenPushPrompt } from '@/utils/kitchenPushPrompt';
import { isStandalonePwa } from '@/utils/pwaStandalone';

const MANIFEST_KITCHEN_LIVE = '/manifest-kitchen-live.json';

/**
 * Küchen-Tablet: only lock navigation in an installed kitchen PWA (standalone).
 * Bind + tablet intent in Safari must not trap the normal partner browser.
 */
export const isKitchenKioskActive = () => {
  if (!isStandalonePwa()) {
    return false;
  }
  if (hasKitchenBindToken()) return true;
  if (hasKitchenPwaManifestFlag()) return true;
  return false;
};

export const resolveKitchenKioskTarget = () => {
  if (!isKitchenSetupComplete()) {
    if (shouldUseKitchenInstallWizard()) {
      return { name: 'kitchen.setup' };
    }
    if (needsKitchenPushPrompt()) {
      return { name: 'kitchen.push-prompt' };
    }
    return { name: 'kitchen.pin' };
  }
  if (!hasValidKitchenSession()) {
    return { name: 'kitchen.pin' };
  }
  return { name: 'kitchen.queue' };
};

/**
 * When a kitchen tablet is bound (PWA), only /kueche/* routes are allowed.
 * @returns {import('vue-router').RouteLocationRaw|null}
 */
export const resolveKitchenKioskRedirect = (to) => {
  if (!isKitchenKioskActive()) {
    return null;
  }
  if (isKitchenRoute(to)) {
    return null;
  }
  return resolveKitchenKioskTarget();
};

const shouldUseKitchenPwaHead = (route) => (
  isKitchenKioskActive()
  || (hasKitchenBindToken() && isKitchenRoute(route))
);

export const syncKitchenPwaManifest = (route) => {
  if (typeof document === 'undefined') return;

  const useKitchenHead = shouldUseKitchenPwaHead(route);

  if (useKitchenHead) {
    applyKitchenPwaHead();
    if (hasKitchenBindToken()) {
      markKitchenPwaManifest();
    }
    if (route?.path?.startsWith('/kueche')) {
      document.title = 'Ollaa Küche';
    }
    return;
  }

  restorePartnerPwaHead();
};

/** @deprecated internal name; kitchen install uses head tags without partner manifest */
export const MANIFEST_KITCHEN_LIVE_PATH = MANIFEST_KITCHEN_LIVE;
